import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  Divider,
  Chip,
  Alert,
} from "@mui/material";

import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";

import { functions } from "../Config/firebase";
import { httpsCallable } from "firebase/functions";

import { db } from "../Config/firebase";

import Navbar from "../Components/Navbar/navbar";
import { getUsuarioAtualComTenant, tenantWhere } from "../Utils/tenant";

/* =========================
   LABELS AMIGÁVEIS
========================= */
const camposLabel = {
  codigo: "Código",
  Cliente: "Cliente",
  Fornecedor: "Fornecedor",
  Descricao: "Descrição",
  Ensaios: "Ensaios",
  Status: "Status",
  DataCadastro: "Data de Cadastro",
  LocalColeta: "Local da coleta",
  EntregueCliente: "Entregue pelo cliente",
  ColaboradorColeta: "Colaborador da coleta",
  DescricaoAgregado: "Descrição do agregado",
  CategoriaMaterial: "Categoria do material",
  MassaEspecifica: "Massa específica",
  TodasObras: "Todas as obras",
  ImagensMaterial: "Imagens do material",
};

/* =========================
   UTIL – FORMATAR DATA
========================= */
function formatarDataFirestore(valor) {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (typeof valor === "boolean") return valor ? "Sim" : "Não";

  // Timestamp serializado { seconds, nanoseconds }
  if (
    typeof valor === "object" &&
    typeof valor.seconds === "number"
  ) {
    return new Date(valor.seconds * 1000).toLocaleDateString("pt-BR");
  }

  if (Array.isArray(valor)) {
    return valor.length ? valor.map(formatarDataFirestore).join("; ") : "—";
  }
  if (typeof valor === "object") {
    return Object.entries(valor)
      .map(([campo, conteudo]) => `${camposLabel[campo] || campo}: ${formatarDataFirestore(conteudo)}`)
      .join("; ") || "—";
  }
  return String(valor);
}

export default function Aprovacoes({ open, isMobile }) {
  const [aprovacoes, setAprovacoes] = useState([]);
  const [materiaisCache, setMateriaisCache] = useState({});
  const [solicitantesCache, setSolicitantesCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [motivos, setMotivos] = useState({});
  const [erro, setErro] = useState("");

  const aprovarEdicaoMaterial = httpsCallable(
    functions,
    "aprovarEdicaoMaterial"
  );
  const rejeitarEdicaoMaterial = httpsCallable(
    functions,
    "rejeitarEdicaoMaterial"
  );

  /* =========================
     CARREGAR APROVAÇÕES
  ========================= */
  async function carregar() {
    try {
      setLoading(true);
      setErro("");
      const usuario = await getUsuarioAtualComTenant();

      const q = query(
        collection(db, "aprovacoes"),
        ...tenantWhere(usuario),
        where("status", "==", "pendente")
      );

      const snap = await getDocs(q);

      const dados = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));

      setAprovacoes(dados);

      /* =========================
         CARREGAR MATERIAIS
      ========================= */
      const idsUnicos = [...new Set(dados
        .filter(a => !a.dadosAtuais || Object.keys(a.dadosAtuais).length === 0)
        .map(a => a.materialId || (a.colecao === "tblListaMateriaisCadastrados" ? a.registroId : null))
        .filter(Boolean))];
      const cacheTemp = {};

      await Promise.all(
        idsUnicos.map(async materialId => {
          if (!materialId) return;
          const ref = doc(
            db,
            "tblListaMateriaisCadastrados",
            materialId
          );
          try {
            const snapMat = await getDoc(ref);
            if (snapMat.exists()) {
              cacheTemp[materialId] = snapMat.data();
            }
          } catch (err) {
            console.warn("Não foi possível consultar o material da aprovação", err);
          }
        })
      );

      setMateriaisCache(cacheTemp);
      const solicitantesTemp = {};
      const uids = [...new Set(dados
        .filter(a => !a.solicitadoPor?.nome)
        .map(a => typeof a.solicitadoPor === "string" ? a.solicitadoPor : a.solicitadoPor?.uid)
        .filter(Boolean))];
      await Promise.all(uids.map(async uid => {
        try {
          const usuarioSnap = await getDoc(doc(db, "users", uid));
          if (usuarioSnap.exists()) solicitantesTemp[uid] = usuarioSnap.data();
        } catch (err) {
          console.warn("Não foi possível consultar o solicitante da aprovação", err);
        }
      }));
      setSolicitantesCache(solicitantesTemp);
    } catch (err) {
      console.error(err);
      setErro("Erro ao carregar aprovações pendentes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  /* =========================
   APROVAR
========================= */
async function aprovar(id) {
  try {
    const aprovacao = aprovacoes.find(a => a.id === id);

    if (!aprovacao) return;

    const tipo = aprovacao.tipo || "material";

const ehMaterial =
  tipo === "material" ||
  tipo === "editar_material" ||
  tipo === "edicao_material";

if (ehMaterial) {
  await aprovarEdicaoMaterial({ aprovacaoId: id });
} else {
      const colecaoDestino =
        aprovacao.colecao ||
        (tipo === "cliente" ? "clientes" : tipo === "obra" ? "obras" : "");

      const registroDestino =
        aprovacao.registroId ||
        aprovacao.clienteId ||
        aprovacao.obraId ||
        aprovacao.idRegistro;

      if (!colecaoDestino || !registroDestino) {
        console.error("Aprovação sem colecao ou registroId:", aprovacao);
        setErro("Esta aprovação está sem coleção ou ID do registro.");
        return;
      }

      await updateDoc(
        doc(db, String(colecaoDestino), String(registroDestino)),
        {
          ...aprovacao.dadosSolicitados,
          atualizadoEm: Timestamp.now(),
        }
      );

      await updateDoc(doc(db, "aprovacoes", id), {
        status: "aprovado",
        aprovadoEm: Timestamp.now(),
      });
    }

    await carregar();
  } catch (err) {
    console.error(err);
    setErro("Erro ao aprovar a alteração.");
  }
}

  /* =========================
     REJEITAR
  ========================= */
 async function rejeitar(id) {
  const motivo = motivos[id];

  if (!motivo) {
    alert("Informe o motivo da rejeição.");
    return;
  }

  try {
    const aprovacao = aprovacoes.find(a => a.id === id);

    if (!aprovacao) return;

    const tipo = aprovacao.tipo || "material";

const ehMaterial =
  tipo === "material" ||
  tipo === "editar_material" ||
  tipo === "edicao_material";

if (ehMaterial) {
  await rejeitarEdicaoMaterial({
    aprovacaoId: id,
    motivo,
  });
} else {
      await updateDoc(doc(db, "aprovacoes", id), {
        status: "rejeitado",
        motivoRejeicao: motivo,
        rejeitadoEm: Timestamp.now(),
      });
    }

    await carregar();
  } catch (err) {
    console.error(err);
    setErro("Erro ao rejeitar a alteração.");
  }
}

  /* =========================
     RENDER
  ========================= */
  return (
    <>
      <Navbar open={open} isMobile={isMobile} />

      <Box sx={{ p: 3, mt: 8 }}>
        <Typography variant="h5" gutterBottom>
          Aprovações Pendentes
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {loading && <CircularProgress />}

        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {!loading && aprovacoes.length === 0 && (
          <Typography>Nenhuma aprovação pendente.</Typography>
        )}

        {aprovacoes.map(ap => {
          const materialId = ap.materialId || ap.registroId;
          const possuiDadosAnteriores = ap.dadosAtuais && Object.keys(ap.dadosAtuais).length > 0;
          const material = possuiDadosAnteriores ? ap.dadosAtuais : materiaisCache[materialId] || {};
          const dadosSolicitados = ap.dadosSolicitados || {};
          const solicitante = typeof ap.solicitadoPor === "object" && ap.solicitadoPor
            ? ap.solicitadoPor
            : { uid: ap.solicitadoPor };
          const perfilSolicitante = solicitantesCache[solicitante.uid] || {};
          const nomeSolicitante = solicitante.nome || perfilSolicitante.nome;
          const emailSolicitante = solicitante.email || perfilSolicitante.email;
          const ehCadastroMaterial = ap.colecao === "tblListaMateriaisCadastrados" || !!ap.materialId;
          const camposTecnicos = ["tenantId", "createdAt", "updatedAt", "updatedBy"];

          const camposAlterados = Object.keys(dadosSolicitados).filter(
            campo =>
              !(ehCadastroMaterial && camposTecnicos.includes(campo)) &&
              JSON.stringify(material[campo]) !==
              JSON.stringify(dadosSolicitados[campo])
          );

          return (
            <Paper key={ap.id} sx={{ p: 3, mb: 3 }}>
              {ap.titulo ? (
                <Typography sx={{ fontWeight: 700, mb: 2 }}>
                  {ap.titulo}
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 700 }}>
                    Cliente: {material.Cliente || "—"}
                  </Typography>

                  <Typography sx={{ fontWeight: 700, mb: 2 }}>
                    Fornecedor: {material.Fornecedor || "—"}
                  </Typography>
                </>
              )}

              <Typography variant="body2">
                <strong>Solicitado por:</strong>{" "}
                {nomeSolicitante || emailSolicitante || solicitante.uid || "Usuário não identificado"}
                {nomeSolicitante && emailSolicitante && nomeSolicitante !== emailSolicitante ? ` (${emailSolicitante})` : ""}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Campos Alterados
              </Typography>

              {camposAlterados.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Nenhuma diferença detectada entre os dados atuais e a solicitação.
                </Alert>
              )}

              {camposAlterados.map(campo => {
                const antes = material[campo];
                const depois = dadosSolicitados[campo];

                return (
                  <Box
                    key={campo}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <Chip
                      label={camposLabel[campo] || campo}
                      color="warning"
                      size="small"
                      sx={{ mb: 1 }}
                    />

                    <Typography variant="body2">
                      <strong>Antes:</strong>{" "}
                      {!possuiDadosAnteriores && !materiaisCache[materialId]
                        ? "Dado anterior indisponível"
                        : formatarDataFirestore(antes)}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Depois:</strong>{" "}
                      {formatarDataFirestore(depois)}
                    </Typography>
                  </Box>
                );
              })}

              <Divider sx={{ my: 2 }} />

              <TextField
                fullWidth
                label="Motivo da rejeição (obrigatório para rejeitar)"
                value={motivos[ap.id] || ""}
                onChange={e =>
                  setMotivos({
                    ...motivos,
                    [ap.id]: e.target.value,
                  })
                }
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => aprovar(ap.id)}
                >
                  Aprovar
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => rejeitar(ap.id)}
                >
                  Rejeitar
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </>
  );
}
